/**
 * [10 §2] — App Check no cliente.
 *
 * A API já recusava pedidos sem `X-Firebase-AppCheck` quando
 * `ENFORCE_APP_CHECK=true`; o que faltava era alguém o enviar. Enquanto essa
 * metade não existiu, ligar o enforcement partia a app inteira em vez de a
 * proteger — uma defesa a meio é pior do que nenhuma, porque parece feita.
 *
 * O mesmo desenho do `auth.ts`, pela mesma razão: sem `VITE_RECAPTCHA_SITE_KEY`
 * nada disto se carrega, e a app continua a funcionar por inteiro. Não é só
 * conveniência de desenvolvimento — é o que impede o reCAPTCHA de ser
 * descarregado no browser de quem nunca configurou App Check nenhum.
 */

import type { AppCheck, getToken as GetToken } from 'firebase/app-check';
import { firebaseConfigFromEnv, type FirebaseWebConfig } from './auth.js';

export const APP_CHECK_HEADER = 'X-Firebase-AppCheck';

/**
 * As entradas da decisão, explícitas.
 *
 * Podiam ser lidas de `import.meta.env` aqui dentro, e era assim que estavam.
 * O problema é que cada módulo recebe o seu próprio `import.meta.env`, o que
 * torna esta decisão — a mais importante deste ficheiro — inalcançável a
 * partir de um teste. Passá-las como argumento custa três linhas e devolve a
 * possibilidade de as exercitar.
 */
export interface AppCheckDeps {
  readonly siteKey: string | undefined;
  readonly config: FirebaseWebConfig | null;
  /**
   * Um token de depuração faz o servidor aceitar um browser que não passou
   * pelo reCAPTCHA. É exactamente o que é preciso para desenvolver, e
   * exactamente o que nunca pode ir para produção — por isso tem a sua
   * própria variável e não herda de `DEV`.
   */
  readonly debug: boolean;
}

type Loaded = { appCheck: AppCheck; getToken: typeof GetToken };

export function appCheckClient(deps: AppCheckDeps): () => Promise<Record<string, string>> {
  let pending: Promise<Loaded | null> | null = null;

  const load = async (): Promise<Loaded | null> => {
    const { siteKey, config } = deps;
    if (siteKey === undefined || siteKey.length === 0 || config === null) return null;

    const [app, mod] = await Promise.all([
      import('firebase/app'),
      import('firebase/app-check'),
    ]);
    // O flag tem de estar posto **antes** de `initializeAppCheck`: depois
    // disso já não é lido, e o token de depuração nunca chega a ser emitido.
    if (deps.debug) {
      (globalThis as { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean }).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    }
    const instance = app.getApps().length > 0 ? app.getApp() : app.initializeApp(config);
    const appCheck = mod.initializeAppCheck(instance, {
      provider: new mod.ReCaptchaEnterpriseProvider(siteKey),
      isTokenAutoRefreshEnabled: true,
    });
    return { appCheck, getToken: mod.getToken };
  };

  /**
   * O cabeçalho, ou nada.
   *
   * Falhar a obter o token devolve `{}` e deixa o pedido seguir. A alternativa
   * — rebentar aqui — trocaria um 401 legível do servidor por um erro do
   * cliente sobre uma regra que o servidor talvez nem aplique. Quem decide se
   * o token é obrigatório é a API, e é lá que a decisão deve aparecer.
   */
  return async (): Promise<Record<string, string>> => {
    // Uma segunda inicialização carregaria um segundo widget de reCAPTCHA na
    // página, que o Firebase recusa e o utilizador vê como app partida.
    pending ??= load().catch(() => null);
    const loaded = await pending;
    if (loaded === null) return {};
    try {
      const { token } = await loaded.getToken(loaded.appCheck, false);
      return { [APP_CHECK_HEADER]: token };
    } catch {
      return {};
    }
  };
}

const env = (): Record<string, string | undefined> =>
  (import.meta as { env?: Record<string, string | undefined> }).env ?? {};

let singleton: (() => Promise<Record<string, string>>) | null = null;

export function appCheckHeader(): Promise<Record<string, string>> {
  singleton ??= appCheckClient({
    siteKey: env()['VITE_RECAPTCHA_SITE_KEY'],
    config: firebaseConfigFromEnv(),
    debug: env()['VITE_APPCHECK_DEBUG'] === 'true',
  });
  return singleton();
}

/** Para testes: esquecer a instância carregada. */
export function resetAppCheck(): void {
  singleton = null;
}
